import {
  attachmentSchema,
  categoryListSchema,
  commentPageSchema,
  commentRevisionListSchema,
  commentSchema,
  followStateSchema,
  similarTopicListSchema,
  topicDetailSchema,
  topicOptionSchema,
  topicPageSchema,
  type ChangeTopicStatusRequest,
  type CreateCommentRequest,
  type CreateLinkAttachmentRequest,
  type CreateTopicOptionRequest,
  type CreateTopicRequest,
  type EditCommentRequest,
  type MarkKeyArgumentRequest,
  type Priority,
  type ReplaceParticipantsRequest,
  type ToggleReactionRequest,
  type TopicListView,
  type TopicStatus,
  type UpdateTopicOptionRequest,
  type UpdateTopicRequest,
  type VersionedRequest,
  type WithdrawTopicOptionRequest,
} from '@mfos/shared';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  apiGet,
  apiPatch,
  apiPost,
  apiPostNoContent,
  apiPut,
  withQuery,
} from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

const TOPIC_PAGE_SIZE = 25;
const COMMENT_PAGE_SIZE = 50;

export interface TopicFilters {
  readonly view: TopicListView;
  readonly status?: TopicStatus;
  readonly priority?: Priority;
  readonly categoryId?: string;
  readonly ownerMemberId?: string;
  readonly participantMemberId?: string;
  readonly q?: string;
}

/** Every topic list/search query of a workspace (invalidated by realtime hints). */
export const topicsKey = (workspaceId: string) => [...workspaceKey(workspaceId), 'topics'] as const;
/** Detail, discussion and rounds of one topic share this prefix. */
export const topicKey = (workspaceId: string, topicId: string) =>
  [...workspaceKey(workspaceId), 'topic', topicId] as const;
export const topicDetailKey = (workspaceId: string, topicId: string) =>
  [...topicKey(workspaceId, topicId), 'detail'] as const;
export const topicCommentsKey = (workspaceId: string, topicId: string) =>
  [...topicKey(workspaceId, topicId), 'comments'] as const;
export const categoriesKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'categories'] as const;

export function useCategories(workspaceId: string) {
  return useQuery({
    queryKey: categoriesKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'categories'), { schema: categoryListSchema, signal }),
    staleTime: 5 * 60_000,
  });
}

export function useTopics(workspaceId: string, filters: TopicFilters) {
  return useInfiniteQuery({
    queryKey: [...topicsKey(workspaceId), 'list', filters] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'topics'), {
          view: filters.view,
          status: filters.status,
          priority: filters.priority,
          categoryId: filters.categoryId,
          ownerMemberId: filters.ownerMemberId,
          participantMemberId: filters.participantMemberId,
          q: filters.q,
          cursor: pageParam,
          limit: TOPIC_PAGE_SIZE,
        }),
        { schema: topicPageSchema, signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

/** Duplicate warning while a title is typed; `title` should already be debounced. */
export function useSimilarTopics(workspaceId: string, title: string, excludeTopicId?: string) {
  const trimmed = title.trim();
  return useQuery({
    queryKey: [...topicsKey(workspaceId), 'similar', trimmed, excludeTopicId ?? null] as const,
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'topics', 'similar'), {
          title: trimmed,
          excludeTopicId,
        }),
        { schema: similarTopicListSchema, signal },
      ),
    enabled: trimmed.length >= 3,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useTopic(workspaceId: string, topicId: string, enabled = true) {
  return useQuery({
    queryKey: topicDetailKey(workspaceId, topicId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'topics', topicId), { schema: topicDetailSchema, signal }),
    enabled,
  });
}

export function useCreateTopic(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateTopicRequest) =>
    apiPost(workspacePath(workspaceId, 'topics'), body, topicDetailSchema),
  );
}

export function useUpdateTopic(workspaceId: string, topicId: string) {
  return useWorkspaceMutation(workspaceId, (body: UpdateTopicRequest) =>
    apiPatch(workspacePath(workspaceId, 'topics', topicId), body, topicDetailSchema),
  );
}

export function useReplaceParticipants(workspaceId: string, topicId: string) {
  return useWorkspaceMutation(workspaceId, (body: ReplaceParticipantsRequest) =>
    apiPut(workspacePath(workspaceId, 'topics', topicId, 'participants'), body, topicDetailSchema),
  );
}

export function useChangeTopicStatus(workspaceId: string, topicId: string) {
  return useWorkspaceMutation(workspaceId, (body: ChangeTopicStatusRequest) =>
    apiPost(workspacePath(workspaceId, 'topics', topicId, 'status'), body, topicDetailSchema),
  );
}

export function useFollowTopic(workspaceId: string, topicId: string) {
  return useWorkspaceMutation(workspaceId, (follow: boolean) =>
    apiPost(
      workspacePath(workspaceId, 'topics', topicId, follow ? 'follow' : 'unfollow'),
      undefined,
      followStateSchema,
    ),
  );
}

// --- Discussion ----------------------------------------------------------------------------------

export function useComments(workspaceId: string, topicId: string) {
  return useInfiniteQuery({
    queryKey: topicCommentsKey(workspaceId, topicId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'topics', topicId, 'comments'), {
          cursor: pageParam,
          limit: COMMENT_PAGE_SIZE,
        }),
        { schema: commentPageSchema, signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useCreateComment(workspaceId: string, topicId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateCommentRequest) =>
    apiPost(workspacePath(workspaceId, 'topics', topicId, 'comments'), body, commentSchema),
  );
}

export function useEditComment(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ commentId, body }: { commentId: string; body: EditCommentRequest }) =>
      apiPatch(workspacePath(workspaceId, 'comments', commentId), body, commentSchema),
  );
}

export function useDeleteComment(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ commentId, body }: { commentId: string; body: VersionedRequest }) =>
      apiPost(workspacePath(workspaceId, 'comments', commentId, 'delete'), body, commentSchema),
  );
}

export function useMarkKeyArgument(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ commentId, body }: { commentId: string; body: MarkKeyArgumentRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'comments', commentId, 'key-argument'),
        body,
        commentSchema,
      ),
  );
}

export function useToggleReaction(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ commentId, body }: { commentId: string; body: ToggleReactionRequest }) =>
      apiPost(workspacePath(workspaceId, 'comments', commentId, 'reactions'), body, commentSchema),
  );
}

export function useCommentRevisions(workspaceId: string, commentId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...workspaceKey(workspaceId), 'comment', commentId, 'revisions'] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'comments', commentId, 'revisions'), {
        schema: commentRevisionListSchema,
        signal,
      }),
    enabled,
  });
}

// --- Options / proposals -------------------------------------------------------------------------

export function useCreateOption(workspaceId: string, topicId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateTopicOptionRequest) =>
    apiPost(workspacePath(workspaceId, 'topics', topicId, 'options'), body, topicOptionSchema),
  );
}

export function useUpdateOption(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ optionId, body }: { optionId: string; body: UpdateTopicOptionRequest }) =>
      apiPatch(workspacePath(workspaceId, 'topic-options', optionId), body, topicOptionSchema),
  );
}

export function useWithdrawOption(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ optionId, body }: { optionId: string; body: WithdrawTopicOptionRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'topic-options', optionId, 'withdraw'),
        body,
        topicOptionSchema,
      ),
  );
}

// --- Attachments ---------------------------------------------------------------------------------

/** Link attachments for a topic or a draft decision (`owner` selects the collection). */
export function useAddLinkAttachment(
  workspaceId: string,
  owner: { kind: 'topics' | 'decisions'; id: string },
) {
  return useWorkspaceMutation(workspaceId, (body: CreateLinkAttachmentRequest) =>
    apiPost(
      workspacePath(workspaceId, owner.kind, owner.id, 'attachments'),
      body,
      attachmentSchema,
    ),
  );
}

export function useDetachAttachment(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (attachmentId: string) =>
    apiPostNoContent(workspacePath(workspaceId, 'attachments', attachmentId, 'detach')),
  );
}
